/**
 * Catalog page requests in flight in this browser tab: a View Next/Previous,
 * a Back/Forward refetch, or a scroll or intent prefetch
 * (pages/store/category/category.vue fetchDisplayPage).
 *
 * A product card's hover prefetch of its PDP reads this and holds back while
 * a page request runs (QS7): the two share a 2-vCPU backend, and the measured
 * hover requests (product + ModelVariantsV1 per card, 0.3-0.9 s each) ran
 * alongside the catalog request the shopper was waiting for.
 */

/**
 * A request stops counting after this long even if it never settles. Client
 * Apollo requests carry no abort signal, so one hung request used to hold
 * back every hover warm-up until the tab was reloaded. A healthy page request
 * takes about 1 s.
 */
export const CATALOG_FETCH_ACTIVITY_MAX_MS = 15 * 1000;

let inFlight = 0;

/** Counts `request` as in flight until it settles; returns it unchanged. */
export const trackCatalogPageFetch = <T>(request: Promise<T>): Promise<T> => {
  inFlight += 1;
  let counted = true;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const settle = () => {
    if (!counted) return;
    counted = false;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    inFlight = Math.max(0, inFlight - 1);
  };
  timer = setTimeout(settle, CATALOG_FETCH_ACTIVITY_MAX_MS);
  // A separate settled branch: the caller still receives the rejection.
  request.then(settle, settle);
  return request;
};

export const isCatalogPageFetchInFlight = () => inFlight > 0;

/**
 * Waits for a page request another caller already started (`joined`).
 *
 * With `shareFailure` (a View Next/Previous click) its failure is this
 * caller's failure, shown at once with the pager's Retry: sending the same
 * request again right away only doubled the wait (TL-3). Without it (a
 * Back/Forward visit, a full load: no inline Retry, and a failure empties the
 * grid) the caller gets one more chance: it joins a retry another caller has
 * started meanwhile (whose failure is final), or receives `undefined` and
 * sends its own request.
 */
export const awaitJoinedCatalogFetch = async <T>(
  joined: Promise<T>,
  currentInFlight: () => Promise<T> | undefined,
  shareFailure: boolean
): Promise<T | undefined> => {
  try {
    return await joined;
  } catch (error) {
    if (shareFailure) throw error;
    const retry = currentInFlight();
    return retry && retry !== joined ? await retry : undefined;
  }
};
